/**
 * The recorded-visit API (backend controllers/visit-recording): one route,
 * three actions. Every action is the caller's own recording on a plan they
 * are on; the server checks both.
 */
import { apiPost } from './apiClient';
import { getSessionIds } from '../store/actions/_sessionIds';

export type SummarySection = { heading: string; items: string[] };
export type SummaryVisibility = { mode: 'plan' | 'admins' | 'people'; people: string[] };

export interface VisitSummary {
  document_id: number;
  filename: string;
  markdown: string;
  title: string | null;
  sections: SummarySection[];
  visibility: SummaryVisibility;
  created_at?: string;
}

export interface VisitRecordingStatus {
  state: 'recording' | 'processing' | 'ready' | 'unknown';
  recording_id: string;
  transcript_document_id?: number;
  segments_done?: number;
  segment_count?: number;
  summary_status?: 'complete' | 'failed';
  duration_seconds?: number;
  started_at?: string | null;
  event_id?: string | null;
  doctor_label?: string | null;
  appointment_label?: string | null;
  summary?: VisitSummary | null;
  transcript?: string;
  /** True only once every version of the recording's audio is confirmed deleted. */
  audio_deleted?: boolean;
}

const path = (projectId: number | string) => {
  const { accountId, cognitoId } = getSessionIds();
  return accountId && cognitoId ? `/visit-recording/${projectId}/${accountId}/${cognitoId}` : null;
};

async function post<T>(projectId: number | string, body: Record<string, unknown>): Promise<{ ok: boolean; payload?: T; message?: string }> {
  try {
    const route = path(projectId);
    if (!route) return { ok: false, message: 'Not signed in.' };
    const res = await apiPost(route, body);
    return res?.success ? { ok: true, payload: res.payload as T } : { ok: false, message: res?.message, payload: res?.payload };
  } catch (err: any) {
    return { ok: false, message: err?.message || 'Network error.' };
  }
}

/** The server makes the recording id (and the row that will hold it). */
export function startVisitRecordingOnServer(projectId: number | string, input: {
  event_id?: string | null;
  conversation_id?: string | null;
  started_at?: string;
  time_zone?: string;
}) {
  return post<{ recording_id: string; doctor_label: string | null }>(projectId, { action: 'start', ...input });
}

/** Throw a recording away on the server: its parts' rows and audio. */
export function discardVisitRecordingOnServer(projectId: number | string, recordingId: string) {
  return post<{ discarded: boolean; audio_deleted: boolean }>(projectId, { action: 'discard', recording_id: recordingId });
}

/** Which of these calendar events were recorded, and the summary when this member may open it. */
export function fetchRecordedVisits(projectId: number | string, eventIds: string[]) {
  return post<{ recorded: Array<{ event_id: string; summary_document_id: number | null }> }>(projectId, { action: 'recorded', event_ids: eventIds });
}

export function finalizeVisitRecording(projectId: number | string, input: {
  recording_id: string;
  segment_count: number;
  duration_seconds: number;
  event_id?: string | null;
  conversation_id?: string | null;
  started_at?: string;
  time_zone?: string;
}) {
  return post<VisitRecordingStatus>(projectId, { action: 'finalize', ...input });
}

export function fetchVisitRecordingStatus(projectId: number | string, recordingId: string, includeTranscript = false) {
  return post<VisitRecordingStatus>(projectId, { action: 'status', recording_id: recordingId, include_transcript: includeTranscript });
}

/** Who sees the summary: everyone on the plan, chosen members, or only the recorder again. */
export function shareVisitSummary(
  projectId: number | string,
  documentId: number,
  // 'people' adds to who can see it; 'remove' takes people away.
  visibility: { mode: 'plan' } | { mode: 'private' } | { mode: 'people'; people: string[] } | { mode: 'remove'; people: string[] },
) {
  return post<{ document_id: number; visibility: SummaryVisibility }>(projectId, { action: 'share', document_id: documentId, visibility });
}
